import React, { useState, useEffect, useRef, useMemo } from 'react';
import { ChevronDown, MapPin, AlertCircle, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useFeatureFlagEnabled } from 'posthog-js/react';

import { useUserSettingsContext } from '../context/UserSettingsContext';
import { getPositions } from '../utils/positionsApi';
import { getLocationsByOrgId } from '../utils/locationApi';
import { getDepartmentsByOrgId } from '../utils/departmentApi';
import {
  getLaborBudgets,
  createLaborBudget,
  updateLaborBudget,
  laborBudgetCache,
} from '../utils/laborBudgetApi';

import LaborBudgetImport from '../components/Budgeting/LaborBudgetImport';
import LaborDriverTable from '../components/Budgeting/LaborDriverTable';
import LoadingScreen from '../components/LoadingScreen';

const LaborBudgeting = () => {
  const navigate = useNavigate();
  const { isLoading: isLoadingUserSettings, currentOrganization } = useUserSettingsContext();
  const laborBudgetingEnabled = useFeatureFlagEnabled('labor-budgeting');

  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [positions, setPositions] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [budgetData, setBudgetData] = useState([]);
  const [monthlyRevision, setMonthlyRevision] = useState(0);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoadingBudgets, setIsLoadingBudgets] = useState(false);

  // UI helper states
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);

  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Redirect if feature flag disabled
  useEffect(() => {
    if (laborBudgetingEnabled === false) {
      navigate('/dashboard');
    }
  }, [laborBudgetingEnabled, navigate]);

  // Fetch locations when organization ready
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        if (!currentOrganization?.id) return;
        const locs = await getLocationsByOrgId(currentOrganization.id);
        if (!isMountedRef.current) return;
        setLocations(locs);
        if (locs.length > 0 && !locs.some(loc => loc.id === selectedLocation?.id)) {
          setSelectedLocation(locs[0]);
        }
      } catch (err) {
        console.error(err);
        if (isMountedRef.current) setError('Failed to load locations');
      }
    };

    if (!isLoadingUserSettings && currentOrganization) {
      fetchLocations();
    }
  }, [currentOrganization, isLoadingUserSettings, selectedLocation]);

  // Fetch positions and departments whenever organization changes
  useEffect(() => {
    const fetchPositionsData = async () => {
      try {
        if (!currentOrganization?.id) return;
        const pos = await getPositions({ orgId: currentOrganization.id });
        if (isMountedRef.current) setPositions(pos || []);
      } catch (err) {
        console.error(err);
        if (isMountedRef.current) setError('Failed to load positions');
      }
    };

    const fetchDepartmentsData = async () => {
      try {
        if (!currentOrganization?.id) return;
        const depts = await getDepartmentsByOrgId(currentOrganization.id);
        if (isMountedRef.current) setDepartments(depts || []);
      } catch (err) {
        console.error(err);
        if (isMountedRef.current) setError('Failed to load departments');
      }
    };

    fetchPositionsData();
    fetchDepartmentsData();
  }, [currentOrganization]);

  // Convert positions to a format BudgetTable understands (memoized for performance)
  const positionAccounts = useMemo(() => (
    positions.map((p) => ({
      id: p.id,
      accountNumber: p.code || String(p.id),
      accountName: p.name || 'Unnamed Position',
      departmentId: p.departmentId,
    }))
  ), [positions]);

  // Fetch labor budgets whenever filters change
  useEffect(() => {
    let active = true;
    const fetchBudgets = async () => {
      if (!currentOrganization || !selectedLocation) return;
      try {
        setIsLoadingBudgets(true);
        setError(null);
        // Invalidate cache to ensure fresh data when dependencies change
        laborBudgetCache.invalidateCache?.();
        const data = await getLaborBudgets({
          orgId: currentOrganization.id,
          locationId: selectedLocation.id,
          year: selectedYear,
        });
        if (active && isMountedRef.current) setBudgetData(data);
      } catch (err) {
        console.error(err);
        if (active && isMountedRef.current) setError('Failed to load labor budgets');
      } finally {
        if (active && isMountedRef.current) setIsLoadingBudgets(false);
      }
    };

    fetchBudgets();
    return () => { active = false; };
  }, [currentOrganization, selectedLocation, selectedYear]);

  if (isLoadingUserSettings) {
    return <LoadingScreen message="Loading User Settings" />;
  }

  if (laborBudgetingEnabled === null || laborBudgetingEnabled === false) {
    return <div className="flex justify-center items-center h-full">Checking feature availability...</div>;
  }

  const years = Array.from({ length: 8 }, (_, i) => new Date().getFullYear() - 3 + i);

  return (
    <div className="flex flex-col p-6 w-full h-full">
      <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6 flex flex-col h-full overflow-hidden">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-gray-800">
            Labor Budgeting {currentOrganization ? `- ${currentOrganization.name}` : ''}
          </h1>
          <div className="flex items-center gap-2">
            {/* Location Selector */}
            <div className="relative">
              <button
                className="flex items-center gap-2 px-4 py-2 rounded-md border border-gray-200 bg-white text-sm font-medium min-w-[180px]"
                onClick={() => setShowLocationDropdown(!showLocationDropdown)}
              >
                <MapPin size={16} className="text-orange-500" />
                {selectedLocation ? selectedLocation.name : locations.length > 0 ? 'Select Location' : 'No Locations'}
                <ChevronDown size={16} />
              </button>
              {showLocationDropdown && (
                <div className="absolute right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-10 w-full max-h-64 overflow-y-auto">
                  {locations.length > 0 ? (
                    locations.map((loc) => (
                      <button
                        key={loc.id}
                        className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                        onClick={() => {
                          setSelectedLocation(loc);
                          setShowLocationDropdown(false);
                        }}
                      >
                        {loc.name}
                      </button>
                    ))
                  ) : (
                    <div className="px-4 py-2 text-sm text-gray-500">No locations available</div>
                  )}
                </div>
              )}
            </div>

            <label className="text-sm text-gray-600">Budget year
              <select aria-label="Budget year" className="ml-2 px-3 py-2 rounded border border-gray-200" value={selectedYear} onChange={e => setSelectedYear(Number(e.target.value))}>
                {years.map(year => <option key={year} value={year}>{year}</option>)}
              </select>
            </label>
          </div>
        </div>

        {/* Error message */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md flex items-center text-red-700">
            <AlertCircle size={20} className="mr-2 flex-shrink-0" />
            <span>{error}</span>
            <button className="ml-auto text-red-500 hover:text-red-700" onClick={() => setError(null)}>
              Dismiss
            </button>
          </div>
        )}

        {/* Search Field */}
        {selectedLocation && (
          <div className="mb-4">
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search positions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500 bg-white text-gray-900 placeholder-gray-400"
              />
            </div>
          </div>
        )}

        {currentOrganization && selectedLocation && <LaborBudgetImport key={`${currentOrganization.id}:${selectedLocation.id}:${selectedYear}`} orgId={currentOrganization.id} locationId={selectedLocation.id} year={selectedYear} onImported={() => setMonthlyRevision(v => v + 1)} />}
        {/* Budget Table */}
        <div className="flex-1 overflow-hidden">
          {!currentOrganization ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center p-8 bg-gray-50 rounded-lg">
                <h3 className="text-lg font-medium text-gray-700 mb-1">Organization Not Loaded</h3>
                <p className="text-gray-500">Organization details could not be loaded from context.</p>
              </div>
            </div>
          ) : !selectedLocation ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center p-8 bg-gray-50 rounded-lg">
                <MapPin size={24} className="mx-auto mb-2 text-orange-500" />
                <h3 className="text-lg font-medium text-gray-700 mb-1">Select a Location</h3>
                <p className="text-gray-500">Please select a location to view labor budget data</p>
              </div>
            </div>
          ) : isLoadingBudgets ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center p-8">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mb-4"></div>
                <p className="text-gray-600">Loading labor budget data...</p>
              </div>
            </div>
          ) : (
            <LaborDriverTable
              positions={positionAccounts}
              departments={departments}
              budgetData={budgetData}
              location={selectedLocation}
              orgId={currentOrganization.id}
              searchTerm={searchTerm}
              year={selectedYear}
              key={`${currentOrganization.id}:${selectedLocation.id}:${selectedYear}:${monthlyRevision}`}
              api={{
                createBudget: createLaborBudget,
                updateBudget: updateLaborBudget,
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default LaborBudgeting; 